import {BaseBadge, BaseCard, BasePagination, BaseTable} from "@/components";
import type {Column} from "@/components/BaseTable/BaseTable";
import {useTransfers} from "@/hooks/useTransfers";
import type {Transfer} from "@/services/transfers.service";
import {formatCurrency} from "@/utils/format";
import {useState} from "react";

function statusVariant(status: string): "success" | "danger" | "warning" | "info" | "neutral" {
  const map: Record<string, "success" | "danger" | "warning" | "info" | "neutral"> = {
    completed: "success",
    failed: "danger",
    pending: "warning",
    processing: "info",
    cancelled: "neutral",
  };
  return map[status] ?? "neutral";
}

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    completed: "Completado",
    failed: "Fallido",
    pending: "Pendiente",
    processing: "Procesando",
    cancelled: "Cancelado",
  };
  return map[status] ?? status;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("es-CL", {dateStyle: "short", timeStyle: "short"}).format(new Date(iso));
}

export function Transfers() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  const filters = {
    page,
    limit,
    ...(statusFilter ? {status: statusFilter} : {}),
    ...(search ? {search} : {}),
  };

  const {data, isLoading} = useTransfers(filters);

  const items = data?.items ?? [];
  const pagination = data?.pagination ?? {total: 0, page: 1, limit: 20, totalPages: 1};

  const columns: Column<Transfer>[] = [
    {
      key: "id",
      header: "ID",
      render: row => <span className="text-xs font-mono text-(--color-text-secondary)">{row.id.slice(0, 8)}…</span>,
    },
    {
      key: "initiatorUserId",
      header: "Remitente",
      render: row => (
        <span className="text-xs font-mono text-(--color-text-secondary)">{row.initiatorUserId.slice(0, 8)}…</span>
      ),
    },
    {
      key: "toUserId",
      header: "Destinatario",
      render: row => (
        <span className="text-xs font-mono text-(--color-text-secondary)">{row.toUserId.slice(0, 8)}…</span>
      ),
    },
    {
      key: "amountCents",
      header: "Monto",
      render: row => (
        <div>
          <p className="text-sm font-semibold tabular-nums text-(--color-text-primary)">
            {formatCurrency(row.amountCents, row.currency)}
          </p>
          {row.feeCents > 0 && (
            <p className="text-xs text-(--color-text-tertiary)">
              Comisión: {formatCurrency(row.feeCents, row.currency)}
            </p>
          )}
        </div>
      ),
    },
    {
      key: "currency",
      header: "Moneda",
      render: row => <span className="text-sm font-medium">{row.currency}</span>,
    },
    {
      key: "status",
      header: "Estado",
      render: row => (
        <BaseBadge
          variant={statusVariant(row.status)}
          label={statusLabel(row.status)}
          dot
        />
      ),
    },
    {
      key: "createdAt",
      header: "Fecha",
      render: row => <span className="text-xs text-(--color-text-tertiary)">{formatDate(row.createdAt)}</span>,
    },
  ];

  const handleReset = () => {
    setStatusFilter("");
    setSearch("");
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold text-(--color-text-primary)">Transferencias</h1>
        <p className="text-sm text-(--color-text-secondary) mt-1">{pagination.total} transferencias</p>
      </div>

      <BaseCard padding="md">
        <div className="flex items-center gap-4 flex-wrap">
          <input
            type="text"
            placeholder="Buscar por ID…"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 rounded-md border border-(--color-border) bg-(--color-surface) text-sm text-(--color-text-primary) w-60"
          />
          <select
            value={statusFilter}
            onChange={e => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 rounded-md border border-(--color-border) bg-(--color-surface) text-sm text-(--color-text-primary)"
          >
            <option value="">Todos los estados</option>
            <option value="pending">Pendiente</option>
            <option value="processing">Procesando</option>
            <option value="completed">Completado</option>
            <option value="failed">Fallido</option>
            <option value="cancelled">Cancelado</option>
          </select>
          {(search || statusFilter) && (
            <button
              type="button"
              onClick={handleReset}
              className="text-sm text-(--color-primary) hover:underline"
            >
              Limpiar
            </button>
          )}
        </div>
      </BaseCard>

      <BaseCard padding="none">
        <BaseTable<Transfer>
          data={items}
          columns={columns}
          loading={isLoading}
          emptyMessage="No se encontraron transferencias."
        />
        {pagination.totalPages > 1 && (
          <BasePagination
            page={page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={limit}
            onPageChange={setPage}
          />
        )}
      </BaseCard>
    </div>
  );
}
